import { useEffect, useState } from "react";
import { api } from "../../api.js";
import { InlineInput, InlineSelect, InlineTextArea } from "../../components/ui.jsx";
import { commaValues } from "./list-utils.js";

function displayDraft(field, value) {
  if (value == null) return "";
  if (field.type === "multi_select") return value.join(", ");
  if (field.type === "json") return JSON.stringify(value, null, 2);
  return String(value);
}

function parsedDraft(field, draft) {
  if (draft === "") return null;
  if (field.type === "number") return Number(draft);
  if (field.type === "multi_select") return commaValues(draft);
  if (field.type === "json") return JSON.parse(draft);
  return draft;
}

export function FieldCell({ column, entityId, current, onChange, onError }) {
  const field = column.field;
  const [draft, setDraft] = useState(displayDraft(field, current?.value));
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(displayDraft(field, current?.value)), [current?.id, field.id]);

  async function save(nextDraft = draft) {
    let value;
    try { value = parsedDraft(field, nextDraft); } catch { onError(`${field.label} must contain valid JSON`); return; }
    if (JSON.stringify(value) === JSON.stringify(current?.value ?? null)) return;
    const previous = current || null;
    const optimistic = {
      id: `optimistic_${crypto.randomUUID()}`,
      value,
      confidence: null,
      verification_status: "verified",
      provider: "manual",
      actor: { type: "human", name: "You" },
      created_at: new Date().toISOString(),
    };
    onChange(optimistic);
    setBusy(true);
    try {
      const data = await api.setFieldValue({ field_id: field.id, entity_id: entityId, value, provider: "manual", verification_status: "verified" });
      onChange(data.value);
    } catch (error) {
      onChange(previous);
      onError(error.message);
    } finally {
      setBusy(false);
    }
  }

  let editor;
  if (field.type === "boolean") {
    editor = <InlineSelect disabled={busy} value={draft} onChange={(event) => { setDraft(event.target.value); save(event.target.value === "" ? "" : event.target.value === "true"); }}><option value="">—</option><option value="true">Yes</option><option value="false">No</option></InlineSelect>;
  } else if (field.type === "select") {
    editor = <InlineSelect disabled={busy} value={draft} onChange={(event) => { setDraft(event.target.value); save(event.target.value); }}><option value="">—</option>{field.options.map((option) => <option key={option} value={option}>{option}</option>)}</InlineSelect>;
  } else if (field.type === "json") {
    editor = <InlineTextArea disabled={busy} value={draft} placeholder="Add JSON…" onChange={(event) => setDraft(event.target.value)} onBlur={() => save()} />;
  } else {
    const type = ["number", "date", "url"].includes(field.type) ? field.type : "text";
    editor = <InlineInput disabled={busy} type={type} value={draft} placeholder="Add value…" title={field.type === "multi_select" ? `Options: ${(field.options || []).join(", ")}` : undefined} onChange={(event) => setDraft(event.target.value)} onBlur={() => save()} onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }} />;
  }
  return <div className="grid min-w-[140px] gap-0.5">{editor}{current && <small className="truncate px-2 text-[10px] text-muted-foreground">{busy ? "saving…" : `${current.provider || "manual"} · ${current.actor?.name || current.actor?.type || "unknown"}`}</small>}</div>;
}

export function StageCell({ list, row, onChange, onError }) {
  const [busy, setBusy] = useState(false);
  const stages = list.configuration.stages || ["new"];
  const choices = stages.includes(row.stage) ? stages : [row.stage, ...stages];

  async function update(stage) {
    const previous = row.stage;
    onChange(stage);
    setBusy(true);
    try {
      const data = await api.updateListRow(row.id, { stage });
      onChange(data.row.stage);
    } catch (error) {
      onChange(previous);
      onError(error.message);
    } finally {
      setBusy(false);
    }
  }

  return <InlineSelect aria-label={`Stage for ${row.entity.name}`} disabled={busy} value={row.stage} onChange={(event) => update(event.target.value)}>{choices.map((stage) => <option key={stage} value={stage}>{stage}</option>)}</InlineSelect>;
}
